import { useEffect, useRef, useState } from "react";
import { ToastReplyBox } from "@/components/toasts/toast-reply-box";
import { useToastReplyDictation } from "@/components/toasts/use-toast-reply-dictation";
import type {
  WorkToastItem,
  WorkToastQuestionOption,
} from "@/stores/work-toast-store";
import { CompactCursorPillQuestionLayout } from "./compact-cursor-pill-question-layout";
import { useT } from "@/i18n";
import { compactCursorMessages } from "@/i18n/messages/compact-cursor";

export function CompactCursorPillQuestion({
  item,
  hovered,
  alwaysShowReply = false,
  disabled,
  draft,
  onDraftChange,
  onAnswer,
}: {
  item: WorkToastItem;
  hovered: boolean;
  alwaysShowReply?: boolean;
  disabled: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onAnswer: (text: string, optionId: string) => void;
}) {
  const t = useT(compactCursorMessages);
  const question = item.question;
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dictation = useToastReplyDictation({
    id: `cursor-question:${question?.awaitingInputId ?? ""}`,
    value: draft,
    onChange: onDraftChange,
    onSubmit: (text) => onAnswer(text, "other"),
  });
  const { cancelOwnedRecording } = dictation;
  const cancelRef = useRef(cancelOwnedRecording);
  cancelRef.current = cancelOwnedRecording;
  useEffect(() => () => cancelRef.current(), []);
  if (!question) return null;

  const pick = (option: WorkToastQuestionOption) =>
    onAnswer(option.label, option.id);
  const showReply =
    alwaysShowReply ||
    hovered ||
    focused ||
    !!draft ||
    !!dictation.mic?.recording;
  return (
    <CompactCursorPillQuestionLayout
      question={question}
      disabled={disabled}
      onPickOption={pick}
      optionTestIdPrefix="compact-cursor-pill-option"
      testId="compact-cursor-pill-question"
      showReply={showReply}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocused(false);
      }}
      reply={
        <ToastReplyBox
          testId="compact-cursor-pill-reply"
          value={draft}
          onChange={onDraftChange}
          onSend={() => onAnswer(draft, "other")}
          placeholder={t("yourAnswer")}
          sending={disabled}
          mic={dictation.mic}
          textareaRef={textareaRef}
        />
      }
    />
  );
}
